import mapValues from 'lodash/mapValues'
import { v4 } from 'uuid'

import { excludeAbortError } from '@app/errors/AbortError'

import { createMap } from '@app/utils/createMap'
import { createSelector } from '@app/utils/createSelector'
import { isTruthy } from '@app/utils/isTruthy'
import { normalize } from '@app/utils/normalizer'

import type { ApiAcademyLessonRecord } from '@app/packages/api/v2/types/entities'
import type { ApiDataResponse } from '@app/packages/api/v2/types/misc'

import { createReduxSlice } from '@app/store/redux_slice'
import { effectiveAccessTokenSelector } from '@app/store/slices/session'
import type { ThunkAction } from '@app/store/thunk'
import { createThunk } from '@app/store/thunk'

import type { AcademyLessonRecord } from './lib'
import { toCourseBundle } from './lib'
import { academyUserSelector } from './profile'

export type RecordsMode = 'upcoming' | 'history'

// Slices are declared before the selectors/thunks that reference them to avoid a TDZ error
// (createSelector reads slice.selector at module evaluation time).
const recordsModelsSlice = createReduxSlice<{ [id: string]: AcademyLessonRecord }>('academy_records_models')
const recordsModeSlice = createReduxSlice<{ mode: RecordsMode; id: string }>('academy_records_mode')
const upcoming = createModeSlices('upcoming')
const history = createModeSlices('history')

const recordsLoggedInSelector = createSelector([academyUserSelector, effectiveAccessTokenSelector], (user, token) => !!(user?.token || token))

// Switching tab persists a manual choice and bumps `id`, which makes the activated mode's fetch
// selector refetch page 1 (merging, not overwriting). Undefined = no manual choice yet.
export const setRecordsMode = (mode: RecordsMode) => recordsModeSlice.set({ mode, id: v4() })

export const resetRecords = () =>
  createThunk(dispatch => {
    dispatch(recordsModelsSlice.remove())
    dispatch(recordsModeSlice.remove())
    dispatch(upcoming.listSlice.remove())
    dispatch(history.listSlice.remove())
    dispatch(upcoming.stateSlice.remove())
    dispatch(history.stateSlice.remove())
  })

export const upcomingRecordsSelector = createSelector([upcoming.listSlice.selector, recordsModelsSlice.selector], (ids, models) =>
  (ids ?? []).map(id => (models ?? {})[id]).filter(isTruthy)
)

export const historyRecordsSelector = createSelector([history.listSlice.selector, recordsModelsSlice.selector], (ids, models) =>
  (ids ?? []).map(id => (models ?? {})[id]).filter(isTruthy)
)

// No manual choice yet → show upcoming when it has records, otherwise history (both empty ⇒ history).
export const effectiveRecordsModeSelector = createSelector(
  [recordsModeSlice.selector, upcoming.listSlice.selector],
  (mode, upcomingIds): RecordsMode => mode?.mode ?? ((upcomingIds?.length ?? 0) > 0 ? 'upcoming' : 'history')
)

export const activeRecordsSelector = createSelector([effectiveRecordsModeSelector, upcomingRecordsSelector, historyRecordsSelector], (mode, up, hist) =>
  mode === 'upcoming' ? up : hist
)

export const activeRecordsStateSelector = createSelector(
  [effectiveRecordsModeSelector, upcoming.stateSlice.selector, history.stateSlice.selector],
  (mode, up, hist) => (mode === 'upcoming' ? up : hist) ?? DEFAULT_STATE
)

export const fetchUpcomingSelector = makeFetchRecordsSelector('upcoming')
export const fetchHistorySelector = makeFetchRecordsSelector('history')

export function fetchNextRecords(mode: RecordsMode) {
  return createThunk((dispatch, getState) => {
    const { stateSlice } = mode === 'upcoming' ? upcoming : history
    const state = stateSlice.selector(getState())
    if (!state || state.loading || state.page >= state.totalPages) return
    const id = recordsModeSlice.selector(getState())?.id
    return dispatch(fetchRecords(mode, state.page + 1, id))
  })
}

// Merge fresh page-1 ids at the top (keeping the already-loaded tail); append older pages.
export function mergeRecordIds(existing: string[], incoming: string[], page: number): string[] {
  return page === 1 ? [...new Set([...incoming, ...existing])] : [...new Set([...existing, ...incoming])]
}

type RecordsPageState = { page: number; totalPages: number; loading: boolean; error: Error | null; fetched: boolean; loadedId?: string }

const DEFAULT_STATE: RecordsPageState = { page: 0, totalPages: 0, loading: false, error: null, fetched: false }
const PER_PAGE = 10

function createModeSlices(key: RecordsMode) {
  return {
    listSlice: createReduxSlice<string[]>(`academy_records_${key}`),
    stateSlice: createReduxSlice<RecordsPageState>(`academy_records_${key}_state`),
  }
}

// Command selector: the thunk to dispatch to keep this mode's page 1 loaded, or null when we
// can't/needn't load (logged out, already loading, or already up to date for the current id).
function makeFetchRecordsSelector(mode: RecordsMode) {
  const { stateSlice } = mode === 'upcoming' ? upcoming : history
  return createSelector(
    [recordsLoggedInSelector, stateSlice.selector, recordsModeSlice.selector],
    (loggedIn, state, recordsMode): ThunkAction<Promise<void>> | null => {
      if (!loggedIn) return null
      if (state?.loading) return null
      const currentId = recordsMode?.id
      if (!state?.fetched) return fetchRecords(mode, 1, currentId)
      if (recordsMode?.mode === mode && state.loadedId !== currentId) return fetchRecords(mode, 1, currentId)
      return null
    }
  )
}

function fetchRecords(mode: RecordsMode, page: number, id: string | undefined) {
  return createThunk(async (dispatch, getState): Promise<void> => {
    const [{ getAcademyProfileLessonRecords }, { callV2Api }] = await Promise.all([
      import('@app/packages/api/v2/actions/academy'),
      import('@app/store/actions/api'),
    ])
    const { listSlice, stateSlice } = mode === 'upcoming' ? upcoming : history
    const token = academyUserSelector(getState())?.token

    dispatch(stateSlice.update(s => ({ ...DEFAULT_STATE, ...s, loading: true, error: null })))

    try {
      const payload = await dispatch(
        callV2Api(
          getAcademyProfileLessonRecords({
            upcoming: mode === 'upcoming',
            email_token: token,
            order_by: 'start_time',
            order_dir: 'desc',
            page,
            per_page: PER_PAGE,
          })
        )
      )
      const records = convertRecordsData(payload)

      dispatch(recordsModelsSlice.update(models => ({ ...models, ...createMap(records, r => r.id) })))
      dispatch(
        listSlice.update(list =>
          mergeRecordIds(
            list ?? [],
            records.map(r => r.id),
            payload.meta.current_page
          )
        )
      )
      dispatch(
        stateSlice.update(s => ({
          ...DEFAULT_STATE,
          ...s,
          loading: false,
          error: null,
          fetched: true,
          loadedId: id,
          page: Math.max(s?.page ?? 0, payload.meta.current_page),
          totalPages: payload.meta.total_pages,
        }))
      )
    } catch (e) {
      dispatch(stateSlice.update(s => ({ ...DEFAULT_STATE, ...s, loading: false, error: excludeAbortError(e as Error) })))
      throw e
    }
  })
}

const convertRecordsData = (data: ApiDataResponse<ApiAcademyLessonRecord[]>): AcademyLessonRecord[] => {
  const { academy_courses = {}, users = {}, avatars = {}, extended_academy_lessons = {}, academy_lesson_pictures = {} } = normalize(data)
  const storeAvatars = avatars ? mapValues(avatars, a => ({ id: a.id, ...a.attributes })) : {}
  const storeUsers = users
    ? mapValues(users, user => {
        const sitter = user
        const avatars = sitter.relationships.avatars?.data ? sitter.relationships.avatars.data.map(a => storeAvatars[a.id]) : []
        return { id: sitter.id, ...sitter.attributes, avatars }
      })
    : {}

  const courses = mapValues(academy_courses, toCourseBundle)

  const lessons = mapValues(extended_academy_lessons, l => {
    const sitterId = l.relationships.sitter.data?.id
    const sitter = sitterId ? storeUsers[sitterId] : null
    const course = courses[l.relationships.course.data!.id]
    const pictures = (l.relationships.pictures?.data ?? []).map(p => academy_lesson_pictures[p.id]).filter(isTruthy)

    return { id: l.id, ...l.attributes, sitter, course, pictures }
  })

  return data.data.map(r => {
    const lesson = lessons[r.relationships.lesson.data!.id]
    return { id: r.id, ...r.attributes, lesson }
  })
}
